/**
 * MeMoney (MM$) wire format: `money.currency` and balance map keys are `MM$` + issuer user id.
 * @see docs/platform/MM_MONEY_CONTRACT.md (client handoff)
 */

import { formatMonetaryValue, roundMonetaryValue } from './currency';

export const MM_MONEY_PREFIX = 'MM$' as const;

const FIAT_CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'CHF',
  SEK: 'SEK',
  NOK: 'NOK',
  DKK: 'DKK',
};

export function isMmMoneyCurrency(code: string | null | undefined): boolean {
  if (!code || typeof code !== 'string') return false;
  return code.startsWith(MM_MONEY_PREFIX) && code.length > MM_MONEY_PREFIX.length;
}

/** Returns issuer user id after `MM$`, or null if not a valid MM$ code. */
export function parseMmMoneyIssuerId(code: string | null | undefined): string | null {
  if (!isMmMoneyCurrency(code)) return null;
  return code!.slice(MM_MONEY_PREFIX.length);
}

export function makeMmMoneyCurrency(issuerUserId: string): string {
  const id = (issuerUserId || '').trim();
  if (!id) throw new Error('makeMmMoneyCurrency: issuerUserId required');
  return `${MM_MONEY_PREFIX}${id}`;
}

/** Auth user id from common profile shapes (`_id` or `id`). */
export function resolveAuthUserId(
  user: { id?: string; _id?: string } | null | undefined
): string | null {
  const id = String(user?._id || user?.id || '').trim();
  return id || null;
}

/** True when `currencyCode` is the signed-in user's own issuer MM$ key. */
export function isOwnMmMoneyCurrency(
  currencyCode: string | null | undefined,
  userId: string | null | undefined
): boolean {
  if (!currencyCode || !userId) return false;
  try {
    return currencyCode === makeMmMoneyCurrency(userId);
  } catch {
    return false;
  }
}

/**
 * Ensure the user's own MM$ balance row exists so they can mint with issuer currency
 * even when the platform has not yet returned a ledger entry.
 */
export function withOwnMmMoneyBalance(
  balances: Record<string, number> | null | undefined,
  userId: string | null | undefined
): Record<string, number> {
  const base = balances ? { ...balances } : {};
  if (!userId) return base;
  try {
    const selfKey = makeMmMoneyCurrency(userId);
    if (!(selfKey in base)) {
      base[selfKey] = 0;
    }
  } catch {
    /* ignore invalid issuer id */
  }
  return base;
}

/** Issuer MM$ is treated as unlimited for client-side spend checks (platform is issuer-of-record). */
export function hasUnlimitedOwnMmMoneyBalance(
  currencyCode: string | null | undefined,
  userId: string | null | undefined
): boolean {
  return isOwnMmMoneyCurrency(currencyCode, userId);
}

export function getSpendableBalance(
  balances: Record<string, number> | null | undefined,
  currencyCode: string,
  userId: string | null | undefined
): number {
  if (hasUnlimitedOwnMmMoneyBalance(currencyCode, userId)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return balances?.[currencyCode] ?? 0;
}

/** User-visible ticker (not the wire key). */
export function formatMmMoneyDisplayTicker(): string {
  return MM_MONEY_PREFIX;
}

/** Decimal places for display/formatting by wire or ISO currency code. */
export function getDecimalsForCurrencyCode(code: string | null | undefined): number {
  if (!code || typeof code !== 'string') return 2;
  if (isMmMoneyCurrency(code)) return 0;
  if (code.toUpperCase() === 'POINTS') return 0;
  return 2;
}

function isValidIso4217Currency(code: string): boolean {
  try {
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code.toUpperCase(),
    }).format(0);
    return true;
  } catch {
    return false;
  }
}

export interface FormatAmountForCurrencyOptions {
  locale?: string;
}

/**
 * Synchronous amount formatter for fiat ISO codes, MM$ wire keys, and unknown brand codes.
 * MM$ uses 0 decimals and the MM$ ticker; fiat uses Intl when valid.
 */
export function formatAmountForCurrency(
  amount: number,
  currencyCode: string,
  options?: FormatAmountForCurrencyOptions
): string {
  const locale = options?.locale ?? 'en-US';
  const decimals = getDecimalsForCurrencyCode(currencyCode);
  const rounded = roundMonetaryValue(amount, decimals);

  if (isMmMoneyCurrency(currencyCode)) {
    return `${formatMmMoneyDisplayTicker()} ${formatMonetaryValue(rounded, 0)}`;
  }

  if (currencyCode === 'POINTS') {
    return `${formatMonetaryValue(rounded, 0)} pts`;
  }

  const upper = currencyCode.toUpperCase();
  if (isValidIso4217Currency(upper)) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: upper,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(rounded);
  }

  const symbol = FIAT_CURRENCY_SYMBOLS[upper] ?? `${currencyCode} `;
  return `${symbol}${formatMonetaryValue(rounded, decimals)}`;
}

/** Short label for a currency key (MM$ ticker for MeMoney; ISO code otherwise). */
export function formatCurrencyCodeLabel(currencyCode: string): string {
  if (isMmMoneyCurrency(currencyCode)) {
    return formatMmMoneyDisplayTicker();
  }
  return currencyCode.toUpperCase();
}

/**
 * Format a per-currency volume map without invalid cross-currency summation.
 * Single bucket → formatted amount; multiple → joined breakdown (e.g. "$50.00 + MM$ 100").
 */
export function summarizeVolumeByCurrency(
  values: Record<string, number> | null | undefined,
  options?: FormatAmountForCurrencyOptions
): string {
  const entries = Object.entries(values || {})
    .map(([currency, amount]) => ({
      currency,
      amount: roundMonetaryValue(amount, getDecimalsForCurrencyCode(currency)),
    }))
    .filter(({ amount }) => amount !== 0)
    .sort((a, b) => b.amount - a.amount);

  if (entries.length === 0) {
    return formatAmountForCurrency(0, 'USD', options);
  }
  if (entries.length === 1) {
    return formatAmountForCurrency(entries[0].amount, entries[0].currency, options);
  }
  return entries
    .map(({ currency, amount }) => formatAmountForCurrency(amount, currency, options))
    .join(' + ');
}

export interface MmMoneyIssuerView {
  issuerUserId: string;
  currencyIconUrl?: string;
  /** Primary profile display name (when distinct from legal/business name). */
  displayName?: string;
  /** Business / legal name from profile when platform sends it separately from displayName. */
  name?: string;
  username?: string;
  backgroundUrl?: string;
}

/** Title: displayName || name || @username || short id (unless omitIdFallback). */
export function buildMmMoneyIssuerTitle(
  v: Pick<MmMoneyIssuerView, 'displayName' | 'name' | 'username' | 'issuerUserId'>,
  options?: { omitIdFallback?: boolean }
): string {
  const name = (v.displayName || v.name || '').trim();
  if (name) return name;
  const u = (v.username || '').trim();
  if (u) return u.startsWith('@') ? u : `@${u}`;
  if (options?.omitIdFallback) return '';
  const id = v.issuerUserId || '';
  if (id.length > 8) return `…${id.slice(-6)}`;
  return id || '—';
}

/** Canonical MM$ artwork fields (user profile or merchant `settings.mmMoney`). */
export interface MmMoneyBrandingFields {
  backgroundImageUrl?: string;
  currencyIconUrl?: string;
}

/**
 * Normalize a raw `settings.mmMoney` object (or equivalent) including legacy aliases.
 * @see docs/platform/MM_MONEY_CONTRACT.md
 */
export function normalizeMmMoneyBrandingFields(raw: unknown): MmMoneyBrandingFields {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const bgRaw = o.backgroundImageUrl ?? o.backgroundUrl;
  const iconRaw = o.currencyIconUrl ?? o.iconUrl;
  const bg = typeof bgRaw === 'string' ? bgRaw.trim() : '';
  const icon = typeof iconRaw === 'string' ? iconRaw.trim() : '';
  return {
    ...(bg ? { backgroundImageUrl: bg } : {}),
    ...(icon ? { currencyIconUrl: icon } : {}),
  };
}

/**
 * Map GET `/api/v1/currency` payload (single MeMoney row, list, or wrapped) into branding fields for editors.
 */
export function normalizeMmMoneyBrandingFromCurrencyApiPayload(raw: unknown): MmMoneyBrandingFields {
  if (raw == null) return {};

  let row: Record<string, unknown> | undefined;

  if (Array.isArray(raw)) {
    row = raw[0] as Record<string, unknown>;
  } else if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.currencies)) row = o.currencies[0] as Record<string, unknown>;
    else if (Array.isArray(o.data)) row = o.data[0] as Record<string, unknown>;
    else if (o.currency && typeof o.currency === 'object') row = o.currency as Record<string, unknown>;
    else row = o;
  }

  if (!row || typeof row !== 'object') return {};
  return normalizeMmMoneyBrandingFields(row);
}

/**
 * Read issuer MM$ branding from profile payloads (`profile.memoney`, top-level legacy `memoney`, or `settings.mmMoney`).
 * Prefers **`profile.memoney`** (canonical) over legacy nests.
 */
export function extractUserMmMoneyBrandingFromProfile(profile: unknown): MmMoneyBrandingFields {
  if (!profile || typeof profile !== 'object') return {};
  const p = profile as {
    settings?: { mmMoney?: unknown };
    memoney?: unknown;
    profile?: { memoney?: unknown };
  };

  const fromSettings = normalizeMmMoneyBrandingFields(p.settings?.mmMoney);
  const fromTopMemoney = normalizeMmMoneyBrandingFields(p.memoney);
  const fromNestedProfile = normalizeMmMoneyBrandingFields(
    p.profile && typeof p.profile === 'object'
      ? (p.profile as { memoney?: unknown }).memoney
      : undefined
  );

  return {
    backgroundImageUrl:
      fromNestedProfile.backgroundImageUrl ??
      fromTopMemoney.backgroundImageUrl ??
      fromSettings.backgroundImageUrl,
    currencyIconUrl:
      fromNestedProfile.currencyIconUrl ??
      fromTopMemoney.currencyIconUrl ??
      fromSettings.currencyIconUrl,
  };
}

/** Prefer explicitly-set user values; fill gaps from legacy merchant branding. */
export function mergeMmMoneyBrandingPreferUser(
  user: MmMoneyBrandingFields,
  legacy: MmMoneyBrandingFields
): MmMoneyBrandingFields {
  return {
    backgroundImageUrl: user.backgroundImageUrl ?? legacy.backgroundImageUrl,
    currencyIconUrl: user.currencyIconUrl ?? legacy.currencyIconUrl,
  };
}

/**
 * Whether MM$ issuer artwork should be read/written on `merchantProfile.mmMoneyBranding`
 * (dual-write with user `settings.mmMoney`).
 */
export function hasMerchantProfileContextForMmMoney(profile: unknown): boolean {
  if (!profile || typeof profile !== 'object') return false;
  const p = profile as { roles?: unknown; otherProfiles?: { merchant?: unknown } };
  if (Array.isArray(p.roles) && p.roles.includes('MERCHANT')) return true;
  return Boolean(p.otherProfiles?.merchant);
}
